import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'

import classnames from 'classnames'
import { Form, Input, Select, Button, message as antdMessage } from 'antd'
import ChatLayout from '@/components/ChatLayout'
import TextRender from '@/components/TextRender'
import Expense from '@/components/Expense'
import useReducers from '@/store/rewrite'
import http from '@/utils/request'
import topup from '@/utils/topup'
import tryParse from '@/utils/tryParse'

import ArticlePrompt from '@/components/ArticlePrompt'
import styles from './rewrite.module.scss'

export default React.memo(function Rewrite() {
  const [inputValue, setInputValue] = useState()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [state, actions] = useReducers()

  const message = useMemo(() => {
    return state.messages[state.active]
  }, [state.messages, state.active])

  useEffect(() => {
    if (!state.active) {
      return
    }

    http.get(`/api/chat/${state.active}`).then(res => {
      if (!res) {
        form.resetFields()
        setInputValue('')
        return
      }

      actions.setMessages({ [state.active]: res })
      setInputValue(res.content)
      form.setFieldsValue({
        ...res.extra,
        model: res.extra?.model || 'gpt-3.5'
      })
    })
  }, [state.active])

  const submit = useCallback(async (extra) => {
    if (!inputValue) {
      antdMessage.info('请输入文章内容')
      return
    }

    const chatId = state.active
    const content = inputValue

    actions.setMessages({
      [chatId]: {
        content: content,
        extra: extra,
        rewrited: '正在回复...',
        status: 'PENDING'
      }
    })

    const finnalMessage = {
      content,
      extra,
      status: 'PENDING',
      rewrited: ''
    }

    try {
      await http.post('/api/chat/rewrite/create', {
        chatId: state.active,
        extra: extra,
        content: inputValue,
      }, {
        onDownloadProgress: function ({ event }) {
          const xhr = event.target
          const { responseText } = xhr

          if (xhr.status !== 200) {
            return
          }

          const jsonText = `[${responseText.replace(/\}\{/g, '},{')}]`
          const json = tryParse(jsonText)

          if (!json) {
            return
          }

          const rewriting = json.reduce((result, next) => {
            return `${result}${next.data}`
          }, '')

          finnalMessage.rewrited = rewriting

          actions.setMessages({
            [chatId]: { ...finnalMessage }
          })
        },
      })

      const chatDetail = await http.get(`/api/chat/${chatId}`)

      actions.setMessages({
        [chatId]: chatDetail
      })
      setInputValue('')
    } catch(err) {
      actions.setMessages({ [chatId]: { } })

      if (err.response?.data.code === '1001') {
        topup()
      }
    }
  }, [state.active, inputValue])

  const handleInputChange= useCallback((v) => {
    setInputValue(v.target.value)
  }, [])

  const createNewChat = useCallback(async () => {
    const newChat = await http.post('/api/chat/create-from-chat', {
      id: state.active
    })

    actions.setHistories([newChat, ...state.histories])
    actions.setActive(newChat.id)
  }, [state.active, state.histories])

  const corpusOptions = useMemo(() => {
    return [{
      key: '改进所提供文本的拼写、语法、清晰、简洁和整体可读性，同时分解长句，减少重复，并提供改进建议。请只提供文本的更正版本，避免包括解释。',
      label: '改进所提供文本的拼写、语法、清晰、简洁和整体可读性，同时分解长句，减少重复，并提供改进建议。请只提供文本的更正版本，避免包括解释。',
    }, {
      key: '将所提供文本进行翻译',
      label: '将所提供文本进行翻译',
    }, {
      key: '请完善提供的文本并扩展，内容清晰，逻辑严谨。',
      label: '请完善提供的文本并扩展，内容清晰，逻辑严谨。',
    }, {
      key: '请润色提供的文本为一个标准学术化的行文',
      label: '请润色提供的文本为一个标准学术化的行文',
    }, {
      key: '你是[某个主题]的专家。总结提供文本的内容，形成摘要。',
      label: '你是[某个主题]的专家。总结提供文本的内容，形成摘要。',
    }]
  }, [])

  const corpusChange = useCallback(e => {
    form.setFieldsValue({
      instruction: e.key
    })
  }, [])

  return (
    <ChatLayout
      type="rewrite"
      title="文章润色"
      onActiveChange={actions.setActive}
      histories={state.histories}
      active={state.active}
      actions={actions}
    >
      <div className={styles.container}>
        {
          !!message?.rewrited && <>
            <div className={styles.messages}>
              <div className={styles.original}>
                <div className={styles.title}>
                  <span>
                    原文：
                    <Expense content={message.content} expense={message.contentExpense}/>
                  </span>
                </div>
                <div className={styles.content}>
                  <TextRender text={message.content}/>
                </div>
              </div>
              <div className={styles.modified}>
                <div className={styles.title}>
                  <span>
                    润色：
                    <Expense content={message.rewrited === '正在回复...' ? '' : message.rewrited} expense={message.resultExpense}/>
                  </span>
                  {
                    message?.status === 'DONE' &&
                    <Button type="link" onClick={createNewChat}>作为输入继续修改</Button>
                  }
                </div>
                <div className={styles.content}>
                  <TextRender text={message.rewrited}/>
                </div>
              </div>
            </div>
          </>
        }

        {
          !message?.rewrited && <>
          <div className={styles.input}>
            <Input.TextArea
              value={inputValue}
              onChange={handleInputChange}
              placeholder="请输入文章原文"
              showCount
              maxLength={1000}
            />
          </div>
          <div className={styles.footer}>
            <ArticlePrompt
              contentProps={{ name: 'instruction', maxLength: 100, placeholder: '请输入指令（如：修正语法）' }}
              corpusChange={corpusChange}
              corpusOptions={corpusOptions}
              form={form}
              onSubmit={submit}
            />
          </div>
          </>
        }
      </div>
    </ChatLayout>
  )
})
